import React from 'react';
import { IEditorProps } from './Editor';
import { EditorLabel } from './EditorLabel';
import { getChildValue, getPropertyDisplayLabel } from 'components/property-editors/utils/ProviderNodeUtils';
import { ChartEntryMetadataSet } from '@decisions/server/DecisionsFramework/Design/Charts/ChartEntryMetadataSet';
import { ChartEntryFilter } from 'components/charts/filters/ChartEntryFilter';
import { ChartEntryMetadata } from '@decisions/server/DecisionsFramework/Design/Charts/ChartEntryMetadata';
import { useScopeHandlers } from './data-filters/hooks/useScopeHandlers';

interface IChartEntryFilterEditorProps extends IEditorProps {}

// className="dp-chart-entry-filter-editor"

/**
 * Editor for filter metadata, which is configuration ahead of data at design time,
 * and is a combination of user-defined and "dynamic" (from data) entries at runtime.
 *
 * Making edits to a dynamic entry causes it to be stored as a user-defined value.
 *
 * Removing a user-defined value causes it to be rendered as a data-driven one if it's present in the data
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ChartEntryFilterEditor({ child, path, id, onChange }: IChartEntryFilterEditorProps) {
  const value: ChartEntryMetadataSet = getChildValue(child);

  const label = getPropertyDisplayLabel(child);

  const emitValue = (newValue: ChartEntryMetadataSet) => {
    onChange(newValue, path);
  };

  const emitEntries = (entries: ChartEntryMetadata[]) => {
    const newValue = { ...value, Data: entries };
    emitValue(newValue);
  };

  const { personalization } = useScopeHandlers({ value, path, setValue: emitValue, onChange });

  return (
    <EditorLabel editor={child} path={path} htmlFor={id} hideInputLabel={true}>
      <ChartEntryFilter
        title={label}
        entries={value.Data}
        originals={value.Raw}
        onUpdateSet={emitEntries}
        personalization={personalization}
        canConfigureColors={value.CanConfigureColors}
      />
    </EditorLabel>
  );
}
